import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PaymentMethods from './PaymentMethods';
import { patch, post } from '../../Services/http';
import { FormProvider, useForm } from 'react-hook-form';
import CheckoutCart from './CheckoutCart';
import DeliveryMethods from './DeliveryMethods';
import { load as loadCart, load } from '../../Actions/Cart.action';
import { translate } from '../../Services/translation';
import ExceedsBudgetCard from './ExceedsBudgetCard';
import constants from '../../constants';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomerInformationValidator from './CustomerInformationValidator';
import ExternalDocumentField from './ExternalDocumentField';
import DeliveryInformation from './DeliveryInformation';
import { generate } from './CheckoutFieldsFactory';
import PartialShipping from './PartialShipping';

const Checkout = () => {
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const checkout = useSelector((state) => state.checkout);
  const [showPayment, setShowPayment] = useState(false);
  const [freight, setFreight] = useState(false);
  const [freightLoaded, setFreightLoaded] = useState(false);
  const [splitPaymentApproved, setSplitPaymentApproved] = useState(false);
  const paymentMethods = checkout.payload.paymentOptions;
  const checkoutMode = checkout.payload.checkoutMode;
  const deliveryAddressCountries = constants.deliveryAddressCountries;
  const [totalPrice, setTotalPrice] = useState(cart.orderTotal);
  const receiptUrl = checkout.payload.receiptUrl;
  const b2c = constants.checkoutMode.privateCustomers;
  const shouldLockEmailField = checkout.payload.lockEmailField;
  const exceedsMinimumOrderAmount = checkout.payload.exceedsMinimumOrderAmount;

  function generateCustomerDetails() {
    const customerDetails = checkout.payload.customerDetails;

    return {
      firstName: customerDetails.firstName,
      lastName: customerDetails.lastName,
      email: shouldLockEmailField ? customerDetails.email : '',
      phoneNumber: customerDetails.phoneNumber,
    };
  }

  const ref = useRef();

  const isB2C = checkoutMode === b2c;

  function generateBillingAddress() {
    let country =
      constants.countries.length === 1 ? constants.countries[0].value : '';

    if (isB2C) {
      return {
        address1: '',
        address2: '',
        careOf: '',
        zipCode: '',
        city: '',
        country: country,
        state: '',
      };
    }

    const companyAddress = checkout.payload.companyAddresses?.[0];

    if (!companyAddress) {
      return {
        address1: '',
        address2: '',
        careOf: '',
        zipCode: '',
        city: '',
        country: country,
        state: '',
      };
    }

    return {
      address1: companyAddress.address ?? '',
      address2: companyAddress.address2 ?? '',
      careOf: '',
      zipCode: companyAddress.zipCode ?? '',
      city: companyAddress.city ?? '',
      country: companyAddress.country ?? '',
      state: companyAddress.state ?? '',
    };
  }

  function generateDeliveryAddress() {
    let country = '';

    if (isB2C) {
      country =
        constants.countries.length === 1 ? constants.countries[0].value : '';
    }

    const deliveryAddresses = checkout.payload.deliveryAddresses.filter((x) => {
      if (deliveryAddressCountries.length > 0) {
        return deliveryAddressCountries.includes(x.country);
      }

      return true;
    });

    let deliveryAddress = deliveryAddresses?.[0];

    if (deliveryAddress) {
      deliveryAddress.companyName = deliveryAddress?.customData?.companyName;
    }

    return !isB2C && deliveryAddress
      ? deliveryAddress
      : {
          address1: '',
          address2: '',
          careOf: '',
          zipCode: '',
          city: '',
          country: country,
          state: '',
        };
  }

  const addressSchema = {
    address1: yup
      .string()
      .required(translate('forms.required'))
      .max(100, translate('checkout.forms.errors.maxlength', 100)),
    zipCode: yup
      .string()
      .required(translate('forms.required'))
      .max(20, translate('checkout.forms.errors.maxlength', 20)),
    city: yup
      .string()
      .required(translate('forms.required'))
      .max(30, translate('checkout.forms.errors.maxlength', 30)),
    country: yup.string().required(translate('forms.required')),
    state: yup
      .string()
      .max(20, translate('checkout.forms.errors.maxlength', 20))
      .when('country', {
        is: (country) => ['US', 'CA'].includes(country),
        then: (schema) => schema.required(translate('forms.required')),
        otherwise: (schema) => schema.optional(),
      }),
  };

  const schema = yup.object().shape({
    companyName: !isB2C
      ? yup.string().required(translate('forms.required'))
      : undefined,
    externalDocumentNumber: yup
      .string()
      .max(20, translate('checkout.forms.errors.maxlength', 20)),
    customerDetails: yup.object().shape({
      firstName: yup.string().required(translate('forms.required')),
      lastName: yup.string().required(translate('forms.required')),
      email: yup
        .string()
        .email(translate('checkout.forms.email.invalid'))
        .required(translate('forms.required'))
        .matches(
          /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
          translate('checkout.forms.email.invalid')
        ),
      phoneNumber: yup
        .string()
        .matches(
          /^\+?[0-9 ]*$/,
          translate('checkout.forms.phonenumber.invalid')
        )
        .required(translate('forms.required')),
    }),
    billingAddress: yup.object().shape(addressSchema),
    shippingAddress: yup.object().shape(addressSchema),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      partialShipping: false,
      companyName: generateDeliveryAddress().companyName,
      externalDocumentNumber: checkout.payload.externalDocumentNumber,
      paymentMethod: checkout.payload.paymentOptions?.options?.find(
        (x) => x.checked
      )?.key,
      billingAddress: generateBillingAddress(),
      shippingAddress: generateDeliveryAddress(),
      customerDetails: generateCustomerDetails(),
      selectedPaymentMethod: checkout.payload.paymentMethods[0],
      selectedDeliveryMethod: checkout.payload.deliveryMethods[0],
    },
  });

  const paymentMethod = methods.watch('paymentMethod');
  const deliveryMethod = methods.watch('deliveryMethod');
  const shippingAddress = methods.watch('shippingAddress');
  const partialShipping = methods.watch('partialShipping');

  const iframeRef = useRef();

  const isShopSpraySession = checkout.payload.isShopSpraySession;

  const exceedsBudget = freight?.exceedsBudget ?? false;

  useEffect(() => {
    setShowPayment(false);
    setFreightLoaded(false);
  }, [paymentMethod]);

  useEffect(() => {
    if (!freightLoaded) {
      return;
    }

    if (iframeRef.current) {
      iframeRef.current.innerHTML = '';
    }

    setFreightOption(deliveryMethod).then(() => {
      reloadPaymentWidget();
    });
  }, [deliveryMethod]);

  useEffect(() => {
    if (!showPayment) {
      return;
    }

    if (exceedsBudget) {
      reloadPaymentWidget();
    } else {
      if (iframeRef.current) {
        iframeRef.current.innerHTML = '';
      }
    }
  }, [exceedsBudget]);

  useEffect(() => {
    if (exceedsBudget) {
      reloadPaymentWidget();
    } else {
      if (iframeRef.current) {
        iframeRef.current.innerHTML = '';
      }
    }
  }, [splitPaymentApproved]);

  useEffect(() => {
    setSplitPaymentApproved(false);
  }, [showPayment]);

  useEffect(() => {
    setPartialShipping().then((response) => {
      if (paymentMethod === 'credit-card') {
        reloadPaymentWidget();
      }
    });
  }, [partialShipping]);

  useEffect(() => {
    if (cart.totalExclVat !== totalPrice) {
      setTotalPrice(cart.totalExclVat);
      setShowPayment(false);
    }

    if (isShopSpraySession) {
      calculateFreight().then(() => {
        dispatch(loadCart());
      });
    }
  }, [cart.totalExclVat]);

  function reloadPaymentWidget() {
    if (paymentMethod === 'credit-card' || (exceedsBudget && showPayment)) {
      initializePayment(methods.getValues());
    }
  }

  const onSubmit = (data) => {
    if (isShopSpraySession) {
      window.location.href =
        '/shopspray-route/punchout-service/postbackmessage?singleEncoding=true';

      return;
    }

    if (data.paymentMethod === 'invoice') {
      placeOrder(data);
    }
  };

  async function calculateFreight() {
    const response = await post('/api/freight', {
      ...methods.getValues(),
    });

    const data = await response.json();

    if (data.options.length > 0) {
      methods.setValue(
        'deliveryMethod',
        data.options.find((x) => x.checked).key
      );
    }

    setFreight(data);
  }

  async function setFreightOption(value) {
    await patch('/api/freight/', {
      deliveryMethod: value,
      countryCode: shippingAddress.country,
    }).then(() => {
      dispatch(loadCart());
    });
  }

  async function initializePayment(orderData) {
    if (iframeRef.current) {
      iframeRef.current.innerHTML = '';
    }

    const response = await post('/api/payments', orderData);

    const data = await response.json();

    let checkoutOptions = {
      checkoutKey: checkout?.payload?.checkoutKey,
      paymentId: data.paymentId,
      containerId: 'checkout-container-div',
      language: 'en-GB',
      theme: {
        buttonRadius: '5px',
      },
    };

    const netsCheckout = new Dibs.Checkout(checkoutOptions);

    netsCheckout.on('payment-completed', function (response) {
      window.location.href = receiptUrl;
    });
  }

  async function setPartialShipping() {
    await patch('/api/freight/setPartialShipping', {
      ...methods.getValues(),
    }).then(() => {
      dispatch(loadCart());
    });
  }

  function placeOrder(data) {
    post('/api/checkout', data)
      .then((response) => response.json())
      .then((dataFromCheckout) => {
        window.location.href = dataFromCheckout.redirectUrl;
      })
      .catch((error) => {
        console.error('Error fetching data from checkout:', error);
      });
  }

  if (checkout.payload.disableCheckout) {
    return (
      <div className="checkout">
        <div className="row">
          <div className="columns">
            <h2>{translate('checkout.checkoutdisabled')}</h2>
          </div>
        </div>
      </div>
    );
  }

  if (!exceedsMinimumOrderAmount) {
    return (
      <div className="checkout">
        <div className="row">
          <div className="columns">
            <h2>{translate('checkout.minimumorderamount.required')}</h2>
          </div>
        </div>
      </div>
    );
  }

  if (checkout.payload.paymentOptions.options.length === 0) {
    return (
      <div className="checkout">
        <div className="row">
          <div className="columns">
            <h2>{translate('checkout.nopaymentoptions')}</h2>
          </div>
        </div>
      </div>
    );
  }

  if (!checkout.payload.authenticated && !isB2C) {
    return (
      <div className="checkout">
        <div className="row">
          <div className="columns">
            <h2>{translate('checkout.unauthenticated')}</h2>
          </div>
        </div>
      </div>
    );
  }

  if (cart.orderRows.length === 0) {
    return (
      <div className="checkout">
        <div className="row">
          <div className="columns">
            <h2>{translate('checkout.cart.empty')}</h2>
          </div>
        </div>
      </div>
    );
  }

  const checkoutData = generate(isB2C, deliveryAddressCountries);

  return (
    <div className="checkout">
      <div className="row">
        <div className="columns">
          <FormProvider {...methods}>
            <h1 className="checkout__title">{translate('checkout')}</h1>
            <form onSubmit={methods.handleSubmit(onSubmit)} ref={ref}>
              <div className="checkout__grid">
                <div className="checkout__grid-left">
                  <CheckoutCart />
                </div>
                <div className="checkout__grid-right">
                  {isShopSpraySession ? (
                    <>
                      {freight?.options?.length > 0 && (
                        <div className="card">
                          <div className="card__body card__body--large">
                            <div className="checkout__form-container">
                              <DeliveryMethods freight={freight} />
                            </div>
                            {freight.deliveryOptions?.length > 0 && (
                              <PartialShipping
                                deliveryOptions={freight.deliveryOptions}
                              />
                            )}
                          </div>
                        </div>
                      )}
                      <div>
                        <a
                          href="/shopspray-route/punchout-service/postbackmessage?singleEncoding=true"
                          className="checkout__submit-button"
                          style={{ textAlign: 'center' }}
                        >
                          {translate('checkout.forms.complete.purchase')}
                        </a>
                      </div>
                    </>
                  ) : (
                    <>
                      {paymentMethods?.options?.length > 1 && (
                        <div>
                          <PaymentMethods paymentMethods={paymentMethods} />
                        </div>
                      )}

                      <CustomerInformationValidator
                        data={checkoutData}
                        onClick={() => {
                          if (!showPayment) {
                            calculateFreight().then(() => {
                              setFreightLoaded(true);
                              setShowPayment(true);
                              dispatch(loadCart());
                              reloadPaymentWidget();
                            });
                          } else {
                            setFreight(null);
                            setShowPayment(false);
                            setFreightLoaded(false);
                          }
                        }}
                        showPayment={showPayment}
                        schema={schema}
                        isB2C={isB2C}
                      />

                      {showPayment && (
                        <>
                          <DeliveryInformation
                            freight={freight}
                            paymentMethod={paymentMethod}
                          />

                          {paymentMethod === 'invoice' ? (
                            <>
                              <ExternalDocumentField
                                control={methods.control}
                              />
                              {exceedsBudget ? (
                                <>
                                  <ExceedsBudgetCard
                                    splitPaymentApproved={splitPaymentApproved}
                                    setSplitPaymentApproved={
                                      setSplitPaymentApproved
                                    }
                                  />

                                  {splitPaymentApproved && (
                                    <div
                                      ref={iframeRef}
                                      id="checkout-container-div"
                                    ></div>
                                  )}
                                </>
                              ) : (
                                <div>
                                  <p className="checkout__price">
                                    {cart.includeVat
                                      ? translate(
                                          'checkout.form.ordertotalinclvat',
                                          cart.orderTotalWithVat
                                        )
                                      : translate(
                                          'checkout.form.ordertotalexclvat',
                                          cart.orderTotalWithoutVat
                                        )}
                                  </p>

                                  <p className="checkout__vat">
                                    {cart.includeVat &&
                                      translate(
                                        'checkout.form.ordertotalexclvat',
                                        cart.orderTotalWithoutVat
                                      )}
                                  </p>

                                  <button
                                    type="submit"
                                    className="checkout__submit-button"
                                  >
                                    {translate(
                                      'checkout.form.completepurchase'
                                    )}
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            <div
                              ref={iframeRef}
                              id="checkout-container-div"
                            ></div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
